import { useState, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "../../Utility/Input/Input";
import Label from "../../Utility/Label/Label";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import ToggleSwitch from "../../Utility/ToggleSwitch/ToggleSwitch";
import FileDropzone from "../../Utility/FileDropzone/FileDropzone";
import { logger } from "../../utils/logger";
import { toDateOnlyApiParam } from "../../utils/dateUtils";
import { get } from "../../Services/ApiBase";
import {
  projectsListEndpoint,
  buildProjectsListQuery,
} from "../../Redux/Thunks/projectManagementThunkUtils";
import { mapProjectToTableRow } from "../Projects/projectMappers";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import "./clients.css";
import ErrorMsg from "../../Utility/ErrorMsg";
import { AddClientSchema } from "../../FormsValidations/Validations";
import BackArrow from "../../Utility/BackArrow/BackArrow";
import NoDataFound from "../../Utility/NoDataFound/NoDataFound";
import {
  updateClient,
  fetchClientsData,
  fetchClientById,
  fetchClientAttachments,
  uploadClientAttachments,
  getClientAttachmentDownloadUrl,
  deleteClientAttachment,
  fetchSaasModules,
  fetchClientSaasModules,
  setClientSaasModules,
} from "../../Redux/Thunks/clientsThunk";
import { getListFromApiResponse } from "../utils/apiListMappers";
import {
  formToClientUpdatePayload,
  apiClientToFormValues,
  normalizeClientDetailResponseForForm,
  extractAttachmentDownloadUrl,
  extractClientAttachmentsList,
  normalizeClientAttachmentFiles,
  getErrorMessage,
  buildClientsListFetchParams,
  findClientSaasModuleRowByCatalogId,
  mapClientSaasModuleApiRowToFormFields,
  ITP_ACCESS_DROPDOWN_OPTIONS,
} from "./clientsDataUtils";
import { validateUploadFiles } from "../../utils/fileUploadValidation";
import ClientSaasModulesSection from "./ClientSaasModulesSection";
import { RiCloseLine, RiDownloadLine, RiFileLine, RiLoader4Line } from "react-icons/ri";
import toast from "react-hot-toast";
import { showError } from "../../utils/toast";
import { isGlobalApiErrorModalOpen } from "../../utils/globalApiErrorModal";
import { Loader } from "../../Utility/Loader";
import {
  selectClientDetailLoading,
  selectClientDetailError,
  selectClientDetailRaw,
  selectSaasModulesState,
  selectClientSaasModulesState,
  selectClientAttachmentsState,
} from "../../Redux/Slices/clientsSlice";
import { fetchSystemList } from "../../Redux/Thunks/systemListsThunk";
import { selectSystemListItems } from "../../Redux/Slices/systemListsSlice";
import { mapSystemListItemToOption } from "../Issues/issueMappers";

export default function EditClient() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const prevPath = location.state?.previousPath || "/clients";
  const { can } = usePermissions();
  const defaultProjectTab = can(PERMISSION_KEYS.PROJECTS_DASHBOARD_VIEW) ? "dashboard" : "issues";

  const loadingClient = useSelector(selectClientDetailLoading);
  const clientDetailError = useSelector(selectClientDetailError);
  const clientDetailRaw = useSelector(selectClientDetailRaw);
  const saasModulesState = useSelector(selectSaasModulesState);
  const clientSaasState = useSelector(selectClientSaasModulesState);
  const attachmentsState = useSelector(selectClientAttachmentsState);

  const invalidClientDetail = useMemo(() => {
    if (!id || !clientDetailRaw) return false;
    return normalizeClientDetailResponseForForm(clientDetailRaw, id) == null;
  }, [id, clientDetailRaw]);

  const clientFormValues = useMemo(() => {
    if (!id || !clientDetailRaw) return null;
    const normalized = normalizeClientDetailResponseForForm(clientDetailRaw, id);
    if (normalized == null) return null;
    return apiClientToFormValues(normalized);
  }, [id, clientDetailRaw]);

  const fetchError =
    clientDetailError ?? (!loadingClient && invalidClientDetail ? "Client not found." : null);

  const saasModuleList = useMemo(
    () => getListFromApiResponse(saasModulesState.raw),
    [saasModulesState.raw]
  );

  const clientSaasModulesData = useMemo(() => {
    if (clientSaasState.raw == null) return null;
    return getListFromApiResponse(clientSaasState.raw);
  }, [clientSaasState.raw]);

  const attachmentList = useMemo(
    () => extractClientAttachmentsList(attachmentsState.raw),
    [attachmentsState.raw]
  );
  const attachmentListLoading = attachmentsState.loading;

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    unregister,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(AddClientSchema),
    defaultValues: {
      clientName: "",
      qbClientName: "",
      contactPerson: "",
      status: "Active",
      contactEmail: "",
      maxHoursMonthly: "",
      hoursLimitPeriod: "Monthly",
      hoursLimit: "",
      notes: "",
      isSaasClient: false,
      saasPlanName: "",
      saasBillingCycle: "",
      saasModules: {},
      serverHosted: "",
      ipAddress: "",
      dbIpAddress: "",
      dbLanguageId: "",
      mailHosted: "",
      domainName: "",
      dns: "",
      domainRegistrar: "",
      itpAccess: "No",
    },
  });

  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const [uploadKey, setUploadKey] = useState(0);
  const [assignedSaasModuleIds, setAssignedSaasModuleIds] = useState([]);
  const [clientProjects, setClientProjects] = useState([]);
  const [clientProjectsLoading, setClientProjectsLoading] = useState(false);
  const [clientProjectsError, setClientProjectsError] = useState(null);

  const isSaasClientWatch = watch("isSaasClient");

  const dbLanguageList = useSelector((s) => selectSystemListItems(s, "DB_LANGUAGE")) ?? [];
  const dbLanguageOptions = useMemo(
    () => dbLanguageList.map((item) => mapSystemListItemToOption(item, {})),
    [dbLanguageList]
  );

  useEffect(() => {
    dispatch(
      fetchSystemList({
        listType: "DB_LANGUAGE",
        context: "edit-client-db-language",
        force: true,
      })
    );
  }, [dispatch]);

  useEffect(() => {
    setAssignedSaasModuleIds([]);
  }, [id]);

  useEffect(() => {
    dispatch(fetchSaasModules());
  }, [dispatch]);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchClientById(id));
  }, [id, dispatch]);

  // Pre-fill form when client data is loaded
  useEffect(() => {
    if (clientFormValues) {
      reset(clientFormValues);
    }
  }, [clientFormValues, reset]);

  // Fetch client SaaS modules when client is SaaS (GET /clients/:id/saas-modules) for implementationFee, monthlyFee, isActive
  useEffect(() => {
    if (!id || !clientFormValues?.isSaasClient) return;
    dispatch(fetchClientSaasModules(id));
  }, [id, dispatch, clientFormValues?.isSaasClient]);

  // Populate SaaS rows: only modules returned for this client (quick-add list), with form defaults per row
  useEffect(() => {
    if (!clientFormValues || saasModuleList.length === 0) return;
    if (!clientFormValues.isSaasClient) return;
    if (clientSaasState.loading) return;
    if (clientSaasModulesData == null) return;
    const list = Array.isArray(clientSaasModulesData)
      ? clientSaasModulesData
      : getListFromApiResponse(clientSaasModulesData);
    const catalogIdSet = new Set(saasModuleList.map((m) => String(m.id)));
    const idsFromApi = [
      ...new Set(
        (list ?? [])
          .map((row) => String(row.moduleId ?? row.module_id ?? ""))
          .filter((mid) => mid && catalogIdSet.has(mid))
      ),
    ];
    setAssignedSaasModuleIds(idsFromApi);
    idsFromApi.forEach((modId) => {
      const clientMod = findClientSaasModuleRowByCatalogId(list, modId);
      const f = mapClientSaasModuleApiRowToFormFields(clientMod);
      setValue(`saasModules.${modId}.implementationFee`, f.implementationFee);
      setValue(`saasModules.${modId}.monthlyFee`, f.monthlyFee);
      setValue(`saasModules.${modId}.additionalFee`, f.additionalFee);
      setValue(`saasModules.${modId}.billingType`, f.billingType);
      setValue(`saasModules.${modId}.startDate`, f.startDate);
      setValue(`saasModules.${modId}.endDate`, f.endDate);
      setValue(
        `saasModules.${modId}.active`,
        clientMod != null ? !!(clientMod.isActive ?? clientMod.is_active) : true
      );
    });
  }, [clientFormValues, saasModuleList, clientSaasModulesData, clientSaasState.loading, setValue]);

  useEffect(() => {
    if (!isSaasClientWatch && assignedSaasModuleIds.length > 0) {
      assignedSaasModuleIds.forEach((mid) => unregister(`saasModules.${mid}`));
      setAssignedSaasModuleIds([]);
    }
  }, [isSaasClientWatch, assignedSaasModuleIds, unregister]);

  // 3.14 Fetch attachments list when client id is available
  useEffect(() => {
    if (!id) return;
    dispatch(fetchClientAttachments(id));
  }, [id, dispatch]);

  // Projects table (above Attachments) — GET /projects?clientId=:id, kept out of the shared
  // projects Redux slice so it can't overwrite the projects list/dropdown used elsewhere.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setClientProjectsLoading(true);
    setClientProjectsError(null);
    get(
      projectsListEndpoint(false),
      buildProjectsListQuery({ clientId: id, page: 1, limit: 100 }, false)
    )
      .then((response) => {
        if (cancelled) return;
        setClientProjects(getListFromApiResponse(response?.data ?? response));
      })
      .catch(() => {
        if (!cancelled) setClientProjectsError("Failed to load projects.");
      })
      .finally(() => {
        if (!cancelled) setClientProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleOpenProject = (project) => {
    const projectId = project?.id ?? project?.projectId;
    if (!projectId) return;
    navigate(`/projects/${projectId}?tab=${defaultProjectTab}`, {
      state: { previousPath: location.pathname + (location?.search || "") },
    });
  };

  const handleDownloadAttachment = async (att) => {
    const attachmentId = att.id ?? att.attachmentId;
    if (!id || !attachmentId) return;
    try {
      const raw = await dispatch(
        getClientAttachmentDownloadUrl({ clientId: id, attachmentId })
      ).unwrap();
      const url = extractAttachmentDownloadUrl(raw);
      if (url) window.open(url, "_blank");
      else showError("Could not get download URL");
    } catch (e) {
      showError(getErrorMessage(e) || "Failed to get download link");
    }
  };

  const handleDeleteAttachment = async (att) => {
    const attachmentId = att.id ?? att.attachmentId;
    if (!id || !attachmentId) return;
    setDeletingAttachmentId(String(attachmentId));
    try {
      await dispatch(deleteClientAttachment({ clientId: id, attachmentId })).unwrap();
      await dispatch(fetchClientAttachments(id)).unwrap();
      toast.success("Attachment deleted");
    } catch (e) {
      showError(getErrorMessage(e) || "Failed to delete attachment");
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const uploadPendingAttachments = async () => {
    const pendingFiles = normalizeClientAttachmentFiles(attachments);
    if (!id || pendingFiles.length === 0) return true;
    if (pendingFiles.length > 10) {
      toast.error("Maximum 10 files allowed");
      return false;
    }
    const validationError = await validateUploadFiles(pendingFiles, {
      inspectContent: false,
    });
    if (validationError) {
      showError(validationError);
      return false;
    }
    await dispatch(uploadClientAttachments({ clientId: id, files: pendingFiles })).unwrap();
    await dispatch(fetchClientAttachments(id)).unwrap();
    setAttachments([]);
    setUploadKey((k) => k + 1);
    return true;
  };

  const handleUploadNewAttachments = async () => {
    if (!id || attachments.length === 0) return;
    setAttachmentUploading(true);
    try {
      const ok = await uploadPendingAttachments();
      if (ok) toast.success("Attachments uploaded");
    } catch (e) {
      showError(getErrorMessage(e) || "Failed to upload attachments");
    } finally {
      setAttachmentUploading(false);
    }
  };

  const onSubmit = async (data) => {
    if (!id) return;
    setLoading(true);
    setSubmitError("");
    try {
      const payload = formToClientUpdatePayload(data);
      await dispatch(updateClient({ id, clientData: payload })).unwrap();

      if (attachments.length > 0) {
        const uploaded = await uploadPendingAttachments();
        if (!uploaded) {
          setLoading(false);
          return;
        }
      }

      toast.success("Client updated successfully");

      if (payload.isSaasClient && saasModuleList.length > 0) {
        const modules = assignedSaasModuleIds.map((modId) => {
          const row = data.saasModules?.[modId] ?? {};
          return {
            moduleId: modId,
            implementationFee: Number(row.implementationFee) || 0,
            monthlyFee: Number(row.monthlyFee) || 0,
            additionalFee: String(row.additionalFee ?? "").trim() || null,
            billingType: row.billingType || "both",
            startDate: toDateOnlyApiParam(row.startDate),
            endDate: toDateOnlyApiParam(row.endDate),
            isActive: !!row.active,
          };
        });
        try {
          await dispatch(setClientSaasModules({ clientId: id, modules })).unwrap();
        } catch (saasErr) {
          logger.error("Error setting SaaS modules:", saasErr);
          if (!isGlobalApiErrorModalOpen()) {
            showError(
              getErrorMessage(saasErr) || "Client updated but SaaS modules failed to save."
            );
          }
        }
      }

      const listParams =
        location.state?.clientsFetchParams ??
        buildClientsListFetchParams({ page: 1, listingFilter: {} });
      dispatch(fetchClientsData(listParams));
      navigate(prevPath);
    } catch (error) {
      logger.error("Error updating client:", error);
      if (isGlobalApiErrorModalOpen()) return;
      const message = getErrorMessage(error) || "Failed to update client";
      showError(message);
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingClient) {
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        <div className="relative mt-2 w-full min-h-[calc(100vh-12rem)] rounded-sm">
          <Loader variant="content" />
        </div>
      </div>
    );
  }

  if (fetchError || (id && !clientFormValues)) {
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        <NoDataFound message={fetchError || "Client not found."} />
      </div>
    );
  }

  const needsSaas = !!clientFormValues?.isSaasClient;
  const saasModulesReady =
    !saasModulesState.loading && (saasModulesState.raw != null || saasModulesState.error != null);
  const attachmentsReady =
    !attachmentsState.loading && (attachmentsState.raw != null || attachmentsState.error != null);
  const clientSaasReady =
    !needsSaas ||
    (!clientSaasState.loading && (clientSaasState.raw != null || clientSaasState.error != null));

  const pageReady =
    saasModulesReady &&
    !saasModulesState.error &&
    attachmentsReady &&
    clientSaasReady &&
    (!needsSaas || !clientSaasState.error);

  if (!pageReady) {
    const msg =
      saasModulesState.error || attachmentsState.error || (needsSaas && clientSaasState.error)
        ? "Failed to load required form data."
        : null;
    return (
      <div className="p-4">
        <BackArrow to={prevPath} />
        {msg ? (
          <NoDataFound message={msg} />
        ) : (
          <div className="relative mt-2 w-full min-h-[calc(100vh-12rem)] rounded-sm">
            <Loader variant="content" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="w-full">
        <div className="p-0">
          <div className="w-full">
            <div className="flex flex-wrap -mx-4 mt-2 mb-2 items-center justify-between">
              <div className="w-full px-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <BackArrow to={prevPath} />
                  <Typography variant="h4" className="mb-0 text-theme-primary">
                    Edit Client
                  </Typography>
                </div>
              </div>
            </div>

            <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md border border-border-primary shadow-sm mt-2 mb-5">
              <div className="flex-1 p-6">
                <form onSubmit={handleSubmit(onSubmit)}>
                  {submitError && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
                      {submitError}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-4">
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Label htmlFor="clientName" required className="mb-1">
                        Client Name
                      </Label>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("clientName")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.clientName ? "border-badge-danger" : ""
                          }`}
                          id="clientName"
                          placeholder="Enter client name"
                          label=""
                          error={errors.clientName?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.clientName?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Label htmlFor="qbClientName" required className="mb-1">
                        QB Client Name
                      </Label>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("qbClientName")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.qbClientName ? "border-badge-danger" : ""
                          }`}
                          id="qbClientName"
                          placeholder="Enter QB client name"
                          label=""
                          error={errors.qbClientName?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.qbClientName?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Label htmlFor="contactPerson" required className="mb-1">
                        Contact Person
                      </Label>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("contactPerson")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.contactPerson ? "border-badge-danger" : ""
                          }`}
                          id="contactPerson"
                          placeholder="Enter contact person name"
                          label=""
                          error={errors.contactPerson?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.contactPerson?.message} className="mt-1" />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            label="Status"
                            name="status"
                            value={field.value}
                            onChange={field.onChange}
                            options={["Active", "On Hold", "Inactive"]}
                            placeholder="Select Status"
                            error={errors.status?.message}
                            required={true}
                            searchable={false}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="contactEmail"
                        className="font-medium text-text-primary mb-1"
                      >
                        Contact Email
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="email"
                          {...register("contactEmail")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.contactEmail ? "border-badge-danger" : ""
                          }`}
                          id="contactEmail"
                          placeholder="Enter contact email"
                          label=""
                          error={errors.contactEmail?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.contactEmail?.message} className="mt-1" />
                    </div>

                    <div className="w-full min-w-0 xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="maxHoursMonthly"
                        className="font-medium text-text-primary mb-1"
                      >
                        Max Hours Monthly
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="number"
                          {...register("maxHoursMonthly")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.maxHoursMonthly ? "border-badge-danger" : ""
                          }`}
                          id="maxHoursMonthly"
                          placeholder="Enter max hours per month"
                          min="0"
                          label=""
                          error={errors.maxHoursMonthly?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.maxHoursMonthly?.message} className="mt-1" />
                      <Typography
                        variant="caption"
                        className="text-text-secondary block mt-0.5 min-w-0 max-w-full break-words leading-snug"
                      >
                        Changing monthly limit does not affect locked months.
                      </Typography>
                    </div>
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <div className="flex flex-wrap items-center gap-2">
                            <Typography
                              variant="text2"
                              component="label"
                              className="font-medium text-text-primary w-full mb-1 block"
                            >
                              Put client on hold
                            </Typography>
                            <ToggleSwitch
                              checked={field.value === "On Hold"}
                              onChange={(checked) => field.onChange(checked ? "On Hold" : "Active")}
                              size="md"
                              aria-label="Put client on hold"
                            />
                          </div>
                        )}
                      />
                    </div>

                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="isSaasClient"
                        control={control}
                        render={({ field }) => (
                          <div className="flex flex-wrap items-center gap-2">
                            <Label className="w-full mb-1 block">SaaS client</Label>
                            <ToggleSwitch
                              checked={!!field.value}
                              onChange={field.onChange}
                              size="md"
                              aria-label="SaaS client"
                            />
                          </div>
                        )}
                      />
                    </div>

                    {/* SaaS modules — quick add only modules this client uses */}
                    <Controller
                      name="isSaasClient"
                      control={control}
                      render={({ field: { value: isSaasClientVal } }) =>
                        isSaasClientVal && saasModuleList.length > 0 ? (
                          <ClientSaasModulesSection
                            control={control}
                            watch={watch}
                            setValue={setValue}
                            unregister={unregister}
                            saasModuleCatalog={saasModuleList}
                            assignedModuleIds={assignedSaasModuleIds}
                            onAssignedModuleIdsChange={setAssignedSaasModuleIds}
                          />
                        ) : null
                      }
                    />

                    {/* Server Hosted */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="serverHosted"
                        className="font-medium text-text-primary mb-1"
                      >
                        Server Hosted
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("serverHosted")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="serverHosted"
                          placeholder="Enter server hosted"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* Web IP Address */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="ipAddress"
                        className="font-medium text-text-primary mb-1"
                      >
                        Web IP Address
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("ipAddress")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="ipAddress"
                          placeholder="Enter web IP address"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* DB IP Address */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="dbIpAddress"
                        className="font-medium text-text-primary mb-1"
                      >
                        DB IP Address
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("dbIpAddress")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="dbIpAddress"
                          placeholder="Enter DB IP address"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* DB Language */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        className="font-medium text-text-primary block mb-1"
                      >
                        DB Language
                      </Typography>
                      <Controller
                        name="dbLanguageId"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            name="dbLanguageId"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target?.value ?? "")}
                            options={dbLanguageOptions}
                            placeholder="Search and select DB language"
                            searchable
                            noWrapper
                            className="text-sm w-full"
                          />
                        )}
                      />
                    </div>

                    {/* Mail Hosted */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="mailHosted"
                        className="font-medium text-text-primary mb-1"
                      >
                        Mail Hosted
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("mailHosted")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="mailHosted"
                          placeholder="Enter mail hosted"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* Domain Name */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="domainName"
                        className="font-medium text-text-primary mb-1"
                      >
                        Domain Name
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("domainName")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="domainName"
                          placeholder="Enter domain name"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* DNS */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="dns"
                        className="font-medium text-text-primary mb-1"
                      >
                        DNS
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("dns")}
                          className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                          id="dns"
                          placeholder="Enter DNS"
                          label=""
                          noWrapper={true}
                        />
                      </div>
                    </div>

                    {/* Domain Registrar */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="domainRegistrar"
                        className="font-medium text-text-primary mb-1"
                      >
                        Domain Registrar
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <Input
                          type="text"
                          {...register("domainRegistrar")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.domainRegistrar ? "border-badge-danger" : ""
                          }`}
                          id="domainRegistrar"
                          placeholder="Enter domain registrar"
                          maxLength={255}
                          label=""
                          error={errors.domainRegistrar?.message}
                          noWrapper={true}
                        />
                      </div>
                      <ErrorMsg message={errors.domainRegistrar?.message} className="mt-1" />
                    </div>

                    {/* ITP Access */}
                    <div className="w-full xl:w-[calc(50%-0.5rem)] md:w-[calc(50%-0.5rem)] mb-2">
                      <Controller
                        name="itpAccess"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            label="ITP Access"
                            name="itpAccess"
                            value={field.value}
                            onChange={field.onChange}
                            options={ITP_ACCESS_DROPDOWN_OPTIONS}
                            placeholder="Select ITP Access"
                            searchable={false}
                            clearable={false}
                            preserveOptionOrder={true}
                            className="text-sm"
                          />
                        )}
                      />
                    </div>

                    {/* Projects — read-only list of this client's projects */}
                    <div className="w-full mb-2">
                      <Typography
                        variant="text2"
                        className="font-medium text-text-primary mb-2 block"
                      >
                        Projects
                      </Typography>
                      {clientProjectsError ? (
                        <div className="text-sm text-red-600 py-2 mb-2">{clientProjectsError}</div>
                      ) : clientProjectsLoading ? (
                        <div className="text-sm text-text-secondary py-2">Loading projects…</div>
                      ) : clientProjects.length > 0 ? (
                        <div className="overflow-x-auto mb-3">
                          <table className="w-full border-collapse mb-0 clients-table">
                            <thead className="bg-surface-secondary">
                              <tr>
                                <th className="content-cell text-start align-middle">Project</th>
                                <th className="content-cell text-start align-middle">Start Date</th>
                                <th className="content-cell text-start align-middle">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {clientProjects.map((project) => {
                                const row = mapProjectToTableRow(project);
                                const projectId = project?.id ?? project?.projectId;
                                return (
                                  <tr key={projectId}>
                                    <td className="text-start align-middle">
                                      <Typography
                                        variant="span1"
                                        onClick={() => handleOpenProject(project)}
                                        className="cursor-pointer underline hover:opacity-80 focus:outline-none text-text-link"
                                      >
                                        {row.projectName}
                                      </Typography>
                                    </td>
                                    <td className="text-start align-middle">
                                      <Typography variant="span1">
                                        {row.startDateDisplay}
                                      </Typography>
                                    </td>
                                    <td className="text-start align-middle">
                                      <Typography
                                        variant="span1"
                                        className={`badge rounded-full px-3 py-1 text-white text-xs font-bold ${row.statusColorClass}`}
                                      >
                                        {row.status}
                                      </Typography>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-sm text-text-secondary py-2 mb-2">
                          No projects for this client yet.
                        </div>
                      )}
                    </div>

                    {/* 3.14 Attachments: list, 3.16 download, 3.17 delete, upload new (max 10, 10MB) */}
                    <div className="w-full mb-2">
                      <Typography
                        variant="text2"
                        className="font-medium text-text-primary mb-2 block"
                      >
                        Attachments
                      </Typography>
                      {attachmentsState.error ? (
                        <div className="text-sm text-red-600 py-2 mb-2">
                          {getErrorMessage(attachmentsState.error) || "Failed to load attachments."}
                        </div>
                      ) : null}
                      {attachmentListLoading ? (
                        <div className="text-sm text-text-secondary py-2">Loading attachments…</div>
                      ) : attachmentList.length > 0 ? (
                        <ul className="space-y-2 mb-3">
                          {attachmentList.map((att) => {
                            const attId = att.id ?? att.attachmentId;
                            const name =
                              att.name ?? att.fileName ?? att.originalName ?? "Attachment";
                            return (
                              <li
                                key={attId}
                                className="flex items-center justify-between p-2 bg-surface-secondary border border-border-primary rounded-md group"
                              >
                                <div className="flex items-center gap-2 min-w-0 flex-1  ">
                                  <RiFileLine className="text-text-secondary shrink-0" />
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadAttachment(att)}
                                    className="text-left min-w-0 flex-1 text-brand-primary hover:underline truncate"
                                  >
                                    {name}
                                  </button>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadAttachment(att)}
                                    className="shrink-0 !p-1"
                                    title="Download"
                                    disabled={deletingAttachmentId === String(attId)}
                                  >
                                    <RiDownloadLine className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeleteAttachment(att)}
                                    className="shrink-0 !p-1"
                                    title={
                                      deletingAttachmentId === String(attId)
                                        ? "Deleting…"
                                        : "Delete"
                                    }
                                    disabled={deletingAttachmentId === String(attId)}
                                  >
                                    {deletingAttachmentId === String(attId) ? (
                                      <RiLoader4Line className="w-4 h-4 animate-spin" aria-hidden />
                                    ) : (
                                      <RiCloseLine className="w-4 h-4" aria-hidden />
                                    )}
                                  </Button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <div className="text-sm text-text-secondary py-2 mb-2">
                          No attachments yet.
                        </div>
                      )}

                      <FileDropzone
                        key={uploadKey}
                        label="Add more attachments (max 10 files, 10MB each)"
                        onFilesChange={(files) => setAttachments(files)}
                        maxFiles={10}
                        maxSizeMB={10}
                        inspectContent={false}
                      />
                      {attachments.length > 0 && (
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          disabled={attachmentUploading}
                          onClick={handleUploadNewAttachments}
                          className="mt-2"
                        >
                          {attachmentUploading
                            ? "Uploading…"
                            : `Upload ${attachments.length} file(s)`}
                        </Button>
                      )}
                    </div>

                    <div className="w-full mb-2">
                      <Typography
                        variant="text2"
                        component="label"
                        htmlFor="notes"
                        className="font-medium text-text-primary mb-1"
                      >
                        Notes
                      </Typography>
                      <div className="relative flex flex-wrap items-stretch w-full">
                        <textarea
                          {...register("notes")}
                          className={`w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                            errors.notes ? "border-badge-danger" : ""
                          }`}
                          id="notes"
                          placeholder="Enter notes"
                          rows="3"
                        />
                      </div>
                      <ErrorMsg message={errors.notes?.message} className="mt-1" />
                    </div>
                  </div>

                  <div className="flex flex-wrap -mx-4 mt-3">
                    <div className="w-full px-4 text-end">
                      <div className="flex gap-2 justify-end">
                        <Button
                          type="button"
                          variant="danger"
                          size="md"
                          disabled={loading}
                          onClick={() => navigate(prevPath)}
                          className="btn-reset"
                        >
                          Cancel
                        </Button>
                        <FormSubmitButton
                          loading={loading}
                          label="Update Client"
                          idleIconClass="ri-save-line"
                          size="md"
                          className="inline-block text-white font-bold transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
